import { z } from 'zod';
/**
 * Pending 2FA challenge cookie lifetime in seconds — shared so the frontend countdown
 * can't drift from auth.instance.ts's `twoFactorCookieMaxAge`.
 */
export const TWO_FACTOR_COOKIE_MAX_AGE_SECONDS = 600;
/**
 * Versioned key ids for better-auth's `secrets` option. Platform and console share one
 * system-DB TwoFactor row, so both apps must be able to decrypt what the other encrypted;
 * these ids are stored in each ciphertext envelope and must never be renumbered.
 */
export const AUTH_SECRET_VERSION = { PLATFORM: 1, CONSOLE: 2 };
/**
 * Builds better-auth's `secrets` array so platform-api and console-api can each decrypt
 * two-factor secrets and backup codes enrolled through the other. Cookie signing uses the
 * first entry only, so session isolation between the apps is unchanged.
 */
export function buildAuthSecrets(input) {
    const { owner, ownSecret, otherSecret } = input;
    const otherOwner = owner === 'PLATFORM' ? 'CONSOLE' : 'PLATFORM';
    const secrets = [{ version: AUTH_SECRET_VERSION[owner], value: ownSecret }];
    if (otherSecret) {
        secrets.push({ version: AUTH_SECRET_VERSION[otherOwner], value: otherSecret });
    }
    return secrets;
}
/**
 * Accepts either an ISO string or a Date object and normalises to an ISO string.
 * better-auth returns Date objects for timestamp fields; this schema handles both.
 */
const DateAsIsoString = z.union([
    z.string(),
    z.date().transform((dateValue) => dateValue.toISOString()),
]);
/** The role assigned to a user within the organisation. */
export const UserRoleSchema = z.enum(['OWNER', 'ADMIN', 'MEMBER']);
/**
 * Raw user shape returned by better-auth's getSession(). Does NOT include tenant context
 * — those fields are added by SessionGuard.
 */
export const BetterAuthUserSchema = z.object({
    id: z.string(),
    email: z.string(),
    name: z.string(),
    emailVerified: z.boolean(),
    image: z.string().nullable(),
    role: UserRoleSchema,
    isActive: z.boolean(),
    isSystemAdmin: z.boolean(),
    /**
     * Console only; absent on tenant sessions and on sessions from before the field
     * existed.
     */
    canDownloadBackups: z.boolean().optional(),
    /** Optional because sessions from before the twoFactor plugin was registered lack it. */
    twoFactorEnabled: z.boolean().optional(),
    createdAt: DateAsIsoString,
    updatedAt: DateAsIsoString,
});
const SessionObjectSchema = z.object({
    id: z.string(),
    token: z.string(),
    userId: z.string(),
    expiresAt: DateAsIsoString,
    createdAt: DateAsIsoString,
    updatedAt: DateAsIsoString,
    ipAddress: z.string().nullable().optional(),
    userAgent: z.string().nullable().optional(),
});
/**
 * Raw session returned by better-auth's getSession(), before SessionGuard enrichment.
 * Used by frontend auth requests (signIn, signUp) to validate the immediate response.
 */
export const BetterAuthSessionSchema = z.object({
    user: BetterAuthUserSchema,
    session: SessionObjectSchema,
});
/**
 * Authenticated user with tenant context, populated by SessionGuard. Extends better-auth
 * user with tenantId and tenantSlug from TenantUser lookup.
 */
export const AuthUserSchema = BetterAuthUserSchema.extend({
    tenantId: z.string(),
    tenantSlug: z.string(),
});
/** Combined session + user object after SessionGuard enrichment. */
export const SessionDataSchema = z.object({
    user: AuthUserSchema,
    session: SessionObjectSchema,
});
//# sourceMappingURL=auth.js.map